/**
 * The frame of the public pages that have no Figma frame of their own yet (the
 * homepage, the legal pages): the Getting in top bar and footer on the ground,
 * light and dark following the device like the sign-in pages.
 */
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { ArrowRight } from "lucide-react";
import { isAuthenticated } from "@/lib/auth";
import { useResolvedTheme } from "@/stores/appearanceStore";
import { cn } from "@/utils/helpers";
import { kitButtonClass } from "@/app/routes/dashboard/components/kit/Button";
import "@/app/routes/dashboard/cockpit.css";
import "./gettingIn.css";
import { Footer, TopBar } from "./parts";

const TERMS_PATHS = ["/terms-of-use", "/terms-of-service"];

/** The legal links (hidden on phones, where the footer carries them) and Sign in, or Open dashboard. */
export function PublicNav() {
  const { pathname } = useLocation();
  const authed = isAuthenticated();
  const links = [
    { to: "/privacy-policy", label: "Privacy Policy", current: pathname === "/privacy-policy" },
    { to: "/terms-of-use", label: "Terms of Service", current: TERMS_PATHS.includes(pathname) },
  ];

  return (
    <nav aria-label="Site" className="flex items-center gap-4">
      {links.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          aria-current={link.current ? "page" : undefined}
          className={cn(
            "unstyled-link inline-flex h-8 items-center rounded-pill px-1 text-[13px] leading-[18px] transition-colors max-sm:hidden",
            link.current ? "font-semibold text-co-text" : "font-medium text-co-text2 hover:text-co-text",
          )}
        >
          {link.label}
        </Link>
      ))}
      <Link
        to={authed ? "/dashboard" : "/login"}
        className={cn("unstyled-link", kitButtonClass({ variant: "secondary" }))}
      >
        {authed ? "Open dashboard" : "Sign in"}
        <ArrowRight aria-hidden className="size-3.5" />
      </Link>
    </nav>
  );
}

export function PublicFrame({ children }: { children: ReactNode }) {
  const theme = useResolvedTheme();
  return (
    <div className={cn("co-shell co-page", theme === "dark" && "dark")}>
      <div className="flex min-h-dvh w-full flex-col bg-co-ground text-co-text">
        <div className="lg:px-4">
          <TopBar action={<PublicNav />} />
        </div>
        <main className="flex flex-1 flex-col">{children}</main>
        <div className="gi-foot">
          <Footer />
        </div>
      </div>
    </div>
  );
}
